import isEqual from 'lodash/isEqual';
import lodashGet from 'lodash/get';
import { StateCreator } from 'zustand';

import {
  addProductToSelected,
  findBundleSettings,
  removeProductToSelected,
} from '../../bundles/CreateBundle/useCreateBundle';
import { getProductsLength } from '../../bundles/bundleUtils';
import { StoreSlices } from '.';

export interface SectionProps {
  id: string;
  name: string;
  bundle: Bundle;
  product: any;
  currencyCode: string;
  variantId: string;
  hideBaBSoldOutOptions: boolean;
  ignoreBaBOutOfStockAllowance: boolean;
  productsInCollection: LiquidProductInCollection[];
  productsInAddonsCollection: GlobalVariant[] | GlobalProduct[];
  individualProductsCollection: LiquidProductInCollection[];
  productsInSections?: BabSection[];
  productsInfo: IndividualProductData[];
  productsLength?: number;
  productBundle?: LiquidProduct;
  selectedProducts: Record<string, number>;
  initialSelectedProducts?: Record<string, number>;
  selectedAddons: Record<string, number>;
  bundleUrlParams: {
    VariantId: string;
  };
  settings: BundleSetting;
  sellingPlans: SellingPlan[];
  sellingPlan: string | number;
  size: number;
  canAddProducts: boolean;
  hasDynamicSize: boolean;
  dynamicVariantId: string;
  minSize: number;
  maxSize: number;
  individualProductsInCollection: GlobalVariant[] | GlobalProduct[];
  bundleSetting: any;
  dynamicDiscountCode: string;
  isAddingToCart: boolean;
}

export interface SectionsSlice extends SectionProps {
  setDynamicVariantId: (dynamicVariantId: string) => void;
  setSelectedProducts: (selectedProducts: Record<string, number>) => void;
  setSelectedAddons: (selectedAddons: Record<string, number>) => void;
  setSellingPlan: (sellingPlan: string | number) => void;
  setVariantId: (variantId: string) => void;
  addProduct: (product: GlobalVariant | GlobalProduct) => void;
  removeProduct: (product: GlobalVariant | GlobalProduct) => void;
  addAddon: (addon: GlobalVariant | GlobalProduct) => void;
  removeAddon: (addon: GlobalVariant | GlobalProduct) => void;
  setIndividualProductsInCollection: (
    individualProductsInCollection: GlobalVariant[] | GlobalProduct[],
  ) => void;
  setBundleSetting: (bundleSetting: any) => void;
  setIsAddingToCart: (isAddingToCart: boolean) => void;
}

export const createSectionsSlice =
  (
    initProps?: Partial<SectionProps>,
  ): StateCreator<
    Partial<StoreSlices>,
    [['zustand/devtools', never], ['zustand/immer', never]],
    [],
    SectionsSlice
  > =>
  (set, get) => ({
    id: initProps?.id ?? '',
    name: initProps?.name ?? '',
    bundle: initProps?.bundle ?? ({} as Bundle),
    product: initProps?.product ?? {},
    currencyCode: initProps?.currencyCode ?? '',
    variantId: initProps?.variantId ?? '',
    hideBaBSoldOutOptions: initProps?.hideBaBSoldOutOptions ?? false,
    ignoreBaBOutOfStockAllowance:
      initProps?.ignoreBaBOutOfStockAllowance ?? false,
    productsInCollection: initProps?.productsInCollection ?? [],
    productsInAddonsCollection: initProps?.productsInAddonsCollection ?? [],
    individualProductsCollection: initProps?.individualProductsCollection ?? [],
    productsInSections: initProps?.productsInSections ?? [],
    productsInfo: initProps?.productsInfo ?? [],
    productsLength: initProps?.productsLength ?? 0,
    productBundle: initProps?.productBundle ?? ({} as LiquidProduct),
    selectedProducts: initProps?.selectedProducts ?? {},
    initialSelectedProducts: initProps?.initialSelectedProducts ?? {},
    selectedAddons: {},
    bundleUrlParams: initProps?.bundleUrlParams ?? { VariantId: '' },
    settings: initProps?.settings ?? ({} as BundleSetting),
    sellingPlans: initProps?.sellingPlans ?? [],
    sellingPlan: initProps?.sellingPlan ?? '',
    size: initProps?.size ?? 0,
    canAddProducts: initProps?.canAddProducts ?? false,
    hasDynamicSize: initProps?.hasDynamicSize ?? false,
    dynamicVariantId: initProps?.dynamicVariantId ?? '',
    dynamicDiscountCode: initProps?.dynamicDiscountCode ?? '',
    minSize: initProps?.minSize,
    maxSize: initProps?.maxSize,
    isAddingToCart: false,
    bundleSetting: initProps?.bundleSetting ?? {},
    individualProductsInCollection:
      initProps?.individualProductsInCollection ?? [],
    setBundleSetting: (bundleSetting) =>
      set(
        (state) => {
          state.sections.bundleSetting = bundleSetting;
        },
        undefined,
        'sections/setBundleSetting',
      ),
    setIsAddingToCart: (isAddingToCart) =>
      set(
        (state) => {
          state.sections.isAddingToCart = isAddingToCart;
        },
        undefined,
        'sections/setIsAddingToCart',
      ),
    setDynamicVariantId: (dynamicVariantId) =>
      set(
        (state) => {
          state.sections.dynamicVariantId = dynamicVariantId;
        },
        undefined,
        'sections/setDynamicVariantId',
      ),
    setDynamicDiscountCode: (dynamicDiscountCode) =>
      set(
        (state) => {
          state.sections.dynamicDiscountCode = dynamicDiscountCode;
        },
        undefined,
        'sections/setDynamicDiscountCode',
      ),
    addProduct: (product: GlobalVariant | GlobalProduct) =>
      set(
        (state) => {
          const selectedProducts = get().sections.selectedProducts;
          const productsLength = get().sections.productsLength;
          const size = get().sections.size;
          const maxProductsToAdd =
            size - productsLength < 1 ? 1 : size - productsLength;
          const settings = get().sections.settings;
          const stepSize = +lodashGet(settings, ['StepSize'], 1);
          const step =
            stepSize > maxProductsToAdd ? maxProductsToAdd : stepSize;
          const newSelectedProducts = addProductToSelected(
            product,
            selectedProducts,
            step,
          );
          const newProductsLength = getProductsLength(newSelectedProducts);
          state.sections.selectedProducts = newSelectedProducts;

          const { maxSize, bundle, minSize } = get().sections;
          const { Type: bundleType } = bundle;

          const isDynamic = bundleType === 'dynamicBaB';
          const hasDynamicSize = isDynamic && minSize !== maxSize;
          const canAddProducts =
            isDynamic && hasDynamicSize
              ? newProductsLength < maxSize
              : newProductsLength < size;
          //TODO cache derived from state props
          state.sections.canAddProducts = canAddProducts;
          state.sections.productsLength = newProductsLength;
        },
        undefined,
        'sections/addProduct',
      ),
    removeProduct: (product: GlobalVariant | GlobalProduct) =>
      set(
        (state) => {
          const selectedProducts = get().sections.selectedProducts;
          const size = get().sections.size;
          const newSelectedProducts = removeProductToSelected(
            product,
            selectedProducts,
          );
          const newProductsLength = getProductsLength(newSelectedProducts);
          state.sections.selectedProducts = newSelectedProducts;

          const { maxSize, bundle, minSize } = get().sections;
          const { Type: bundleType } = bundle;
          const isDynamic = bundleType === 'dynamicBaB';
          const hasDynamicSize = isDynamic && minSize !== maxSize;
          state.sections.productsLength =
            getProductsLength(newSelectedProducts);
          const canAddProducts =
            isDynamic && hasDynamicSize
              ? newProductsLength < maxSize
              : newProductsLength < size;
          //TODO cache derived from state props
          state.sections.canAddProducts = canAddProducts;
          state.sections.productsLength = newProductsLength;
        },
        undefined,
        'sections/removeProduct',
      ),
    addAddon: (product: GlobalVariant | GlobalProduct) =>
      set(
        (state) => {
          const selectedAddons = get().sections.selectedAddons;
          const newSelectedAddons = addProductToSelected(
            product,
            selectedAddons,
          );

          state.sections.selectedAddons = newSelectedAddons;
        },
        undefined,
        'sections/addAddon',
      ),
    removeAddon: (product: GlobalVariant | GlobalProduct) =>
      set(
        (state) => {
          const selectedAddons = get().sections.selectedAddons;
          const newSelectedAddons = removeProductToSelected(
            product,
            selectedAddons,
          );
          state.sections.selectedAddons = newSelectedAddons;
        },
        undefined,
        'sections/removeAddon',
      ),

    setSellingPlan: (sellingPlan) =>
      set(
        (state) => {
          state.sections.sellingPlan = sellingPlan;
        },
        undefined,
        'sections/setSellingPlan',
      ),
    setIndividualProductsInCollection: (individualProductsInCollection) =>
      set(
        (state) => {
          state.sections.individualProductsInCollection =
            individualProductsInCollection;
        },
        undefined,
        'sections/setIndividualProductsInCollection',
      ),
    setSelectedProducts: (selectedProducts) =>
      set(
        (state) => {
          const currentSelectedProducts = get().sections.selectedProducts;
          if (isEqual(currentSelectedProducts, selectedProducts)) return;
          state.sections.selectedProducts = selectedProducts;

          //TODO cache derived from state props
          state.sections.productsLength = getProductsLength(selectedProducts);
        },
        undefined,
        'sections/setSelectedProducts',
      ),
    setSelectedAddons: (selectedProducts) =>
      set(
        (state) => {
          state.sections.selectedAddons = selectedProducts;
        },
        undefined,
        'sections/setSelectedAddons',
      ),
    setVariantId: (variantId) =>
      set(
        (state) => {
          state.sections.variantId = variantId;
          const { minSize, maxSize, size, bundle } = get().sections;
          const { Type: bundleType, Settings: bundleSettings } = bundle;

          //TODO cache derived from state props
          const settings = findBundleSettings({
            settings: bundleSettings,
            variantId,
          }) as BundleSetting;

          const isDynamic = bundleType === 'dynamicBaB';

          const hasDynamicSize = isDynamic && minSize !== maxSize;
          const productsLength = get().sections.productsLength;
          const canAddProducts =
            isDynamic && hasDynamicSize
              ? productsLength < maxSize
              : productsLength < size;

          //TODO cache derived from state props
          state.sections.canAddProducts = canAddProducts;
          state.sections.settings = settings;
          state.sections.size = size;
          state.sections.hasDynamicSize = hasDynamicSize;
          state.sections.dynamicDiscountCode = settings?.DynamicDiscountCode;
        },
        undefined,
        'sections/setVariantId',
      ),
  });
